import { useState, ReactNode } from 'react';
import { Waypoint } from 'react-waypoint';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { trackShare } from 'lib/Tracking';
import { fetchListings, ListingsResponse } from 'lib/AutolistAPI';
import Sharing from 'components/Sharing';
import Categories from 'components/Comparison/Categories';
import ReportCard from 'components/Comparison/ReportCard';
import UserTake from 'components/Comparison/UserTake';
import UserTakeEmpty from 'components/Comparison/UserTakeEmpty';
import VehiclePhotos from 'components/Comparison/VehiclePhotos';
// eslint-disable-next-line import/no-cycle
import StickyBarVehicle from 'components/Comparison/StickyBarVehicle';
// eslint-disable-next-line import/no-cycle
import LineItems from 'components/Comparison/LineItems';
// eslint-disable-next-line import/no-cycle
import AvailableListings from 'components/Comparison/AvailableListings';
import Markdown from 'components/ContentfulMarkdown';
import ComparisonInterface from 'types/Apis/Comparison';

// styles
import styles from 'components/Article/styles';
import { sectionCSS, columnStyles } from 'components/styles/comparisons';
import { screen, colors } from 'components/styles/variables';
import reportcardLogo from 'public/static/images/comparisons/reportcard-logo.svg';
import reportcardLogoEs from 'public/static/images/comparisons/reportcard-logo-es.svg';
import { useAsync } from 'react-use';
import { format } from 'date-fns';
import { compact } from 'lodash';

type Listings = Record<string, any>;

const StickyBar = ({
  comparison,
  listings,
}: {
  comparison: ComparisonInterface;
  listings: Listings;
}): JSX.Element | null => {
  if (!listings.vehicle1 || !listings.vehicle2) {
    return null;
  }
  return (
    <div className="bar">
      <section className="columns">
        <StickyBarVehicle
          vehicle={comparison.vehicle1}
          listingsCount={listings.vehicle1.total_count}
        />
        <StickyBarVehicle
          vehicle={comparison.vehicle2}
          listingsCount={listings.vehicle2.total_count}
        />
      </section>
      <style jsx>
        {`
          .bar {
            width: 100vw;
            position: fixed;
            bottom: 0;
            left: 0;
            box-shadow: 0 4px 6px rgba(0, 0, 4, 0.2), 0 -4px 6px rgba(0, 0, 0, 0.2);
            justify-content: space-between;
            display: flex;
            z-index: 9999;
            background-color: white;
          }

          section {
            display: flex;
            justify-content: space-between;
            width: 100%;
          }

          @media (min-width: ${screen.smMin}) {
            section {
              font-size: 0.5em;
              width: 960px;
              margin: 0 auto;
            }
          }
        `}
      </style>
      <style jsx>{sectionCSS}</style>
    </div>
  );
};

export function mmyUrlFromVehicle({
  make,
  model,
  year,
}: {
  make?: string;
  model?: string;
  year?: number | string;
}): string {
  return `/${compact([make, model, year])
    .map((token) => token.toString().replace(/\W/g, '+'))
    .join('-')
    .toLowerCase()}`;
}

function ReportCardSection({ comparison }: { comparison: ComparisonInterface }): JSX.Element {
  const { t, i18n } = useTranslation();

  function localizedReportcardLogo(): string {
    if (i18n.language === 'es') {
      return reportcardLogoEs;
    }

    return reportcardLogo;
  }
  return (
    <section className="report-card-section">
      <header>
        <h3>
          {t('comparison.reportcard.count', {
            count: comparison.metaGrade_source_count,
          })}
        </h3>
        <img className="logo" src={localizedReportcardLogo()} />
      </header>
      <hr />
      <div className="columns">
        <ReportCard metaGrade={comparison.vehicle1.meta_grade} />
        <ReportCard metaGrade={comparison.vehicle2.meta_grade} />
      </div>
      <style jsx>{sectionCSS}</style>
      <style jsx>{columnStyles}</style>
      <style jsx>{`
        .logo {
          width: 250px;
        }

        header {
          margin-bottom: -15px;
        }
      `}</style>
    </section>
  );
}

function UsersTakeSection({ comparison }: { comparison: ComparisonInterface }): JSX.Element {
  const { t } = useTranslation();

  return (
    <section>
      <header>
        <h3>{t('comparison.userTake.header')}</h3>
        <h2>{t('comparison.userTake.whatOwnersThink')}</h2>
      </header>
      <hr />
      <div className="columns">
        {[comparison.vehicle1, comparison.vehicle2].map((vehicle) => {
          const {
            id,
            recommended_owner_reviews: recommendedOwnerReviews,
            total_owner_reviews: totalOwnerReviews,
          } = vehicle;
          return vehicle.total_owner_reviews && vehicle.total_owner_reviews > 0 ? (
            <UserTake key={id} recommended={recommendedOwnerReviews} total={totalOwnerReviews} />
          ) : (
            <UserTakeEmpty key={id} />
          );
        })}
      </div>
      <style jsx>{sectionCSS}</style>
      <style jsx>{columnStyles}</style>
      <style jsx>{`
        section {
          margin-top: 25px;
        }

        h2 {
          margin-top: -5px;
          margin-bottom: -15px;
        }
      `}</style>
    </section>
  );
}

export function Section({
  children,
  rowClassName = 'row--small-gutters-xs',
}: {
  children: ReactNode;
  rowClassName?: string;
}): JSX.Element {
  return (
    <div className={`row ${rowClassName}`}>
      <div className="col-lg-8 col-lg-offset-2 col-md-10 col-md-offset-1 col-sm-10 col-sm-offset-1">
        {children}
      </div>
    </div>
  );
}

interface Props {
  comparison: ComparisonInterface;
  shareUrl: string;
}

export default function Comparison({ comparison, shareUrl }: Props): JSX.Element {
  const { t } = useTranslation();
  const [showStickyBar, setShowStickyBar] = useState(false);

  const { value: listings } = useAsync(async () => {
    const fetchVehicle1Listings =
      comparison.vehicle1.make && comparison.vehicle1.model
        ? fetchListings({
            make: comparison.vehicle1.make,
            model: comparison.vehicle1.model,
            year_min: comparison.vehicle1.year,
            year_max: comparison.vehicle1.year,
            location_by: 'geoip',
            fallback_to_nationwide_threshold: 60,
          })
        : Promise.resolve();

    const fetchVehicle2Listings =
      comparison.vehicle2.make && comparison.vehicle2.model
        ? fetchListings({
            make: comparison.vehicle2.make,
            model: comparison.vehicle2.model,
            year_min: comparison.vehicle2.year,
            year_max: comparison.vehicle2.year,
            location_by: 'geoip',
            fallback_to_nationwide_threshold: 60,
          })
        : Promise.resolve();

    const [vehicle1Listings, vehicle2Listings] = await Promise.all<ListingsResponse | undefined>([
      fetchVehicle1Listings,
      fetchVehicle2Listings,
    ]);
    return {
      vehicle1: vehicle1Listings,
      vehicle2: vehicle2Listings,
    };
  });

  const twitterShareText = `${comparison.title} ${shareUrl} via @autolistcom`;

  const sharing = (
    <Sharing
      url={shareUrl}
      emailSubject={comparison.title}
      emailBody={`${t('comparison.emailShare')} ${shareUrl}`}
      twitterText={twitterShareText}
      onShare={(platform): void =>
        trackShare({
          category: 'srp_interaction',
          type: 'comparison',
          platform,
          url: shareUrl,
        })
      }
    />
  );

  const authorName = comparison?.author?.name;
  const authorBylineUrl = comparison?.author?.bylineurl;
  const date = new Date(comparison.publication_date);

  const formattedDate = format(date, 'MMMM d y');

  const { title, categories, vehicle1, vehicle2 } = comparison;
  const shouldShowReportCard = vehicle1.meta_grade || vehicle2.meta_grade;

  return (
    <div className="outer-container">
      <div className="clearfix container article">
        <Section>
          <div className="body">
            <div className="clearfix">
              <Categories categories={categories} />
              <div className="sharing--top pull-right hidden-xs">{sharing}</div>
            </div>
            <h1 className="title">{title}</h1>
            <div className="author-container">
              <span>By </span>
              <span>
                <Link href={`/news-and-analysis${authorBylineUrl}`} legacyBehavior>
                  <a className="author-link">{authorName}</a>
                </Link>
              </span>
              <span> | {formattedDate}</span>
            </div>
            <div className="breaker" />
          </div>
        </Section>
        <Section>
          <VehiclePhotos vehicle1={vehicle1} vehicle2={vehicle2} />
        </Section>
        <Section>{shouldShowReportCard && <ReportCardSection comparison={comparison} />}</Section>
        <Section>
          <UsersTakeSection comparison={comparison} />
        </Section>
        <Section>
          <Markdown className="editorial" source={comparison.content} />
        </Section>
        <Section>
          <Waypoint
            onEnter={(): void => setShowStickyBar(true)}
            onLeave={({ currentPosition }): void => {
              setShowStickyBar(currentPosition !== Waypoint.below);
            }}
          />
          <LineItems comparison={comparison} listings={listings} />
          {showStickyBar && listings ? (
            <StickyBar comparison={comparison} listings={listings} />
          ) : null}
          <Waypoint
            onEnter={(): void => {
              setShowStickyBar(false);
            }}
            onLeave={({ currentPosition }): void => {
              setShowStickyBar(currentPosition === Waypoint.below);
            }}
          />
        </Section>
        {listings && <AvailableListings comparison={comparison} listings={listings} />}
        <Section>
          <div className="sharing --bottom">{sharing}</div>
        </Section>
        <Section>
          <p className="copyright">
            © Certain automotive content displayed within this website, Copyright{' '}
            <a href="https://www.dataonesoftware.com">DataOne Software</a> and are protected under
            the United States and international copyright law. Any unauthorized use, reproduction,
            distribution, recording or modification of this content is strictly prohibited.
          </p>
          <p className="copyright">
            Automotive Images, Copyright [2000 – {new Date().getFullYear()}
            ]. All Rights Reserved. The automotive images contained herein are owned by the
            respective copyright owners and are protected under United States and international
            copyright law. Access to and use of these images is restricted by terms and conditions
            of a separate license agreement. Any unauthorized use, reproduction, distribution,
            recording or modification of these images is strictly prohibited.”
          </p>
        </Section>
      </div>
      <style jsx>{styles}</style>
      <style jsx>{`
        .copyright {
          color: ${colors.G4};
          font-size: 0.8em;
        }

        .copyright a {
          color: inherit;
          font-size: inherit;
          font-weight: inherit;
          font-family: inherit;
          outline: inherit;
        }

        .body {
          margin-top: 45px;
        }

        .article {
          font-weight: 300;
          font-size: 20px;
        }

        .author-link {
          color: ${colors.ALR1};
          font-size: 16px;
        }

        .author-link:hover {
          color: ${colors.ALR1};
          text-decoration: underline;
        }

        .author-container {
          font-size: 16px;
        }

        @media (max-width: ${screen.xsMax}) {
          .body {
            margin-top: 0;
          }
        }

        :global(.editorial) {
          margin-top: 25px;
        }
      `}</style>
    </div>
  );
}
